import React, { useCallback, useState } from 'react'
import { useStyles } from './ListingCanvas.styles'
import MyWebComponent from '../Canvas/west'
import { useFormikContext } from 'formik'
import { productDummyData } from '../../../utils/templateCreationConstants'

import {
  BWAProduct,
  ListingCardInCreation,
} from '../../../modules/storefront/listingCardCreation/reducer'
import { get, isEmpty } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { v4 as uuid4 } from 'uuid'
import {
  DragAndDropCanvas,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'

import clsx from 'clsx'
import { CardMedia, Typography } from '@mui/material'
import { useDispatch, useSelector } from '../../../store'
import { updateAppAlert } from '@labrav/common'
import { failedCopy, sucessCopy } from '../../../common/utils/constants'
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import { solarizedLight } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { faCode } from '@fortawesome/pro-regular-svg-icons'
import { useAccountId } from '@labrav/common'

interface ListingCanvasProps {
  handleSaveClick: (showAlert?: boolean) => void
}

export const ListingCanvas: React.FC<ListingCanvasProps> = ({
  handleSaveClick,
}) => {
  const { classes } = useStyles()
  const { values, setFieldValue } = useFormikContext<ListingCardInCreation>()
  const { listingCardData } = values
  const listingCardCreationFormData = useSelector<ListingCardInCreation>(
    state => state.listingCardInCreation
  )
  const accountId = useAccountId({ product: 'flyout' })

  const { data, ...restListingData }: any = listingCardData?.template || {}
  const { cardHeader, ...restTemplateData } = data || {}

  const updatedListingData = {
    ...restListingData,
    data: {
      ...restTemplateData,
      cardHeader: {
        shouldShowLogoInCardHeader: cardHeader?.shouldShowLogoInCardHeader,
      },
    },
  }

  const integrationId =
    listingCardCreationFormData.listingCardData?.integrationId
  const dispatch = useDispatch()
  const [openlModal, setOpenModal] = useState(false)
  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleOpenModal = () => {
    setOpenModal(true)
  }
  const handleEmbedCode = useCallback(async () => {
    await handleOpenModal()
    await handleSaveClick(false)
  }, [handleSaveClick, handleOpenModal])

  const codeString = `<script
  type="text/javascript"
  src="https://bwa.marketplace.awsstatic.com/assets/partner.js"
></script>
<script
  type="module"
  src="https://widgets.labra.io/HypermarketplaceWidget/assets/js/widget.js"
></script>
<labra-widget
  integration-id=${integrationId} partner-id=${accountId}
></labra-widget>`
  const handleCopyModal = () => {
    navigator.clipboard.writeText(codeString).then(
      () => {
        dispatch(
          updateAppAlert({
            message: sucessCopy,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        handleCloseModal()
      },
      () => {
        dispatch(
          updateAppAlert({
            message: failedCopy,
            messageType: 'ERROR',
            autoClose: true,
          })
        )
      }
    )
  }
  const handleAdd = () => {
    setFieldValue('listingCardData.product', [
      ...listingCardData.product,
      {
        uniqueId: uuid4(),
        url: '',
        isOpen: true,
      },
    ])
  }
  const handleReorder = (
    reorderedItems: { id: string; content: React.ReactNode }[]
  ) => {
    const newCards = reorderedItems.map((item, index) => {
      const card = actualProduct.find(card => card.uniqueId === item.id)
      return {
        ...card,
      }
    })
    setFieldValue('listingCardData.product', newCards)
  }
  const noOps = () => {}
  const { product: actualProduct } = listingCardData
  const items = actualProduct.map((product: BWAProduct, index: number) => ({
    id: product.uniqueId,
    content: !isEmpty(get(product, 'product_id', '')) ? (
      <div className={classes.cardTextContainer}>
        <Typography className={classes.cardHeading}>{`Card ${
          index + 1
        }`}</Typography>
        <MyWebComponent
          int-data={JSON.stringify(get(updatedListingData, 'data', {}))}
          product-details={JSON.stringify(get(product, 'data', {}))}
        />
      </div>
    ) : (
      <div className={classes.cardTextContainer}>
        <Typography className={classes.cardHeading}>{`Card ${
          index + 1
        }`}</Typography>
        <MyWebComponent
          int-data={JSON.stringify(get(updatedListingData, 'data', {}))}
          product-details={JSON.stringify(productDummyData)}
        />
      </div>
    ),
  }))

  return (
    <div
      className={classes.canvas}
      data-testid="canvas-wrapper"
      style={{ backgroundColor: updatedListingData.data.canvasColor }}
    >
      <div
        className={classes.canvasContainerWrapper}
        data-testid="listing-canvas"
        style={{ backgroundColor: updatedListingData.data.canvasColor }}
      >
        <div className={classes.textMediaWrapper}>
          {cardHeader?.shouldShowLogoInCardHeader && (
            <CardMedia
              src={updatedListingData.data.buyWithAwsLogoUrl}
              component="img"
              className={classes.mediaStyle}
            />
          )}
          <Typography
            style={{
              color: cardHeader?.customization?.color,
              fontSize: cardHeader?.customization?.fontSize,
              fontWeight: cardHeader?.customization?.fontWeight,
            }}
          >
            {cardHeader?.title}
          </Typography>
        </div>
        <div className={classes.canvasContainer}>
          <DragAndDropCanvas
            items={items}
            onDragEnd={handleReorder}
            gapBetween={3}
            isHorizontal={false}
          />
          <div
            className={clsx(
              classes.plusIcon,
              actualProduct.length >= 6 ? classes.disabled : ''
            )}
            onClick={actualProduct.length >= 6 ? noOps : handleAdd}
            data-testid="add-icon-canvas"
          >
            <FontAwesomeIcon
              icon={faPlus}
              style={{ fontWeight: 600, width: '34.27px', height: '34.27px' }}
            />
          </div>
        </div>
      </div>
      <ModalConfirmV2
        title={'Copy snippet to embed code'}
        content={
          <SyntaxHighlighter
            language="html"
            style={solarizedLight}
            customStyle={{
              padding: '24px',
              borderRadius: '8px',
              fontSize: '14px',
              lineHeight: '1.5',
              backgroundColor: '#F6F7F9',
            }}
          >
            {codeString}
          </SyntaxHighlighter>
        }
        open={openlModal}
        titleAcceptButton={'Copy'}
        onAccept={handleCopyModal}
        onDecline={handleCloseModal}
        variant={ModalSizeVariants.large}
        icon={
          <FontAwesomeIcon
            icon={faCode}
            style={{ width: '20px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
      />
    </div>
  )
}
